import { createAsyncThunk } from "@reduxjs/toolkit";
import * as recruitmentApi from "../services/recruitmentApi";
import type { InterviewFeedbackPayload } from "./types";

const getError = (error: any): string => {
  return (
    error?.data?.error?.message ||
    error?.data?.message ||
    error?.message ||
    "Something went wrong"
  );
};

export const fetchJobs = createAsyncThunk(
  "recruitment/fetchJobs",
  async (
    filters: {
      search?: string;
      department_id?: string;
      employment_type?: string;
      status?: string;
      page?: number;
      limit?: number;
      date_from?: any;
      date_to?: any;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.listJobs(company_id, company_type, filters);
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchJobDetails = createAsyncThunk(
  "recruitment/fetchJobDetails",
  async (jobId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getJobDetails(
        company_id,
        company_type,
        jobId,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const createJob = createAsyncThunk(
  "recruitment/createJob",
  async (jobData: Record<string, any>, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.createJob(company_id, company_type, jobData);
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateJob = createAsyncThunk(
  "recruitment/updateJob",
  async (
    { jobId, jobData }: { jobId: number; jobData: Record<string, any> },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateJob(
        company_id,
        company_type,
        jobId,
        jobData,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const deleteJob = createAsyncThunk(
  "recruitment/deleteJob",
  async (jobId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      await recruitmentApi.deleteJob(company_id, company_type, jobId);
      return jobId;
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const closeJob = createAsyncThunk(
  "recruitment/closeJob",
  async (
    {
      jobId,
      reason,
      closeOnExternalPlatforms,
    }: {
      jobId: number;
      reason?: string;
      closeOnExternalPlatforms?: boolean;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.closeJob(
        company_id,
        company_type,
        jobId,
        reason,
        closeOnExternalPlatforms,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const unpublishJob = createAsyncThunk(
  "recruitment/unpublishJob",
  async (
    { jobId, platformName }: { jobId: number; platformName: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.unpublishJob(
        company_id,
        company_type,
        jobId,
        platformName,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateJobStatus = createAsyncThunk(
  "recruitment/updateJobStatus",
  async (
    { jobId, status }: { jobId: number; status: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateJob(company_id, company_type, jobId, {
        status,
      });
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchApplications = createAsyncThunk(
  "recruitment/fetchApplications",
  async (
    filters: {
      job_id?: number;
      status?: string;
      source?: string;
      search?: string;
      priority?: string;
      assigned_to?: number;
      min_score?: number;
      max_score?: number;
      date_from?: string;
      date_to?: string;
      page?: number;
      limit?: number;
      sort_by?: string;
      sort_order?: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.listApplications(
        company_id,
        company_type,
        filters,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchApplicationDetails = createAsyncThunk(
  "recruitment/fetchApplicationDetails",
  async (applicationId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getApplicationDetails(
        company_id,
        company_type,
        applicationId,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateApplicationStatus = createAsyncThunk(
  "recruitment/updateApplicationStatus",
  async (
    data: {
      applicationId: number;
      newStatus: string;
      notes?: string;
      candidateMessage?: string;
      sendEmailNotification: boolean;
    },
    { getState, rejectWithValue, dispatch },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      const res = await recruitmentApi.updateApplicationStatus(
        company_id,
        company_type,
        data.applicationId,
        data.newStatus,
        data.notes,
        {
          candidate_message: data.candidateMessage,
          send_email_notification: data.sendEmailNotification
        }
      );
      dispatch(fetchApplicationDetails(data.applicationId));
      return res;
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const scheduleInterviewRound = createAsyncThunk(
  "recruitment/scheduleInterviewRound",
  async (
    data: {
      application_id: number;
      round_type: string;
      round_number: number;
      scheduled_at: string;
      interviewer_ids?: number[];
      duration_minutes?: number;
      interview_mode?: "video_call" | "phone" | "in_person";
      interview_link?: string;
      interview_location?: string;
      send_notification?: boolean;
    },
    { getState, rejectWithValue, dispatch },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      const res = await recruitmentApi.scheduleInterviewRound(
        company_id,
        company_type,
        data,
      );
      dispatch(fetchApplicationDetails(data.application_id));
      return res;
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const bulkUpdateApplicationStatus = createAsyncThunk(
  "recruitment/bulkUpdateStatus",
  async (
    {
      applicationIds,
      newStatus,
    }: { applicationIds: number[]; newStatus: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.bulkUpdateStatus(
        company_id,
        company_type,
        applicationIds,
        newStatus,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const exportApplications = createAsyncThunk(
  "recruitment/exportApplications",
  async (filters: Record<string, any>, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.exportApplications(
        company_id,
        company_type,
        filters,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchInterviews = createAsyncThunk(
  "recruitment/fetchInterviews",
  async (
    filters: {
      application_id?: number;
      status?: string;
      date_from?: string;
      date_to?: string;
      page?: number;
      limit?: number;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.listInterviews(
        company_id,
        company_type,
        filters,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchPendingFeedbacks = createAsyncThunk(
  "recruitment/fetchPendingFeedbacks",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getMyPendingFeedbacks(
        company_id,
        company_type,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchRoundTemplates = createAsyncThunk(
  "recruitment/fetchRoundTemplates",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.listRoundTemplates(company_id, company_type);
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const createRoundTemplate = createAsyncThunk(
  "recruitment/createRoundTemplate",
  async (templateData: any, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.createRoundTemplate(
        company_id,
        company_type,
        templateData,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateRoundTemplate = createAsyncThunk(
  "recruitment/updateRoundTemplate",
  async (
    { templateId, templateData }: { templateId: number; templateData: any },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateRoundTemplate(
        company_id,
        company_type,
        templateId,
        templateData,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const deleteRoundTemplate = createAsyncThunk(
  "recruitment/deleteRoundTemplate",
  async (templateId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      await recruitmentApi.deleteRoundTemplate(
        company_id,
        company_type,
        templateId,
      );
      return templateId;
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchJobRounds = createAsyncThunk(
  "recruitment/fetchJobRounds",
  async (jobId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getJobRounds(company_id, company_type, jobId);
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const saveJobRounds = createAsyncThunk(
  "recruitment/saveJobRounds",
  async (
    { jobId, rounds }: { jobId: number; rounds: any[] },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.saveJobRounds(
        company_id,
        company_type,
        jobId,
        rounds,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchMyReferrals = createAsyncThunk(
  "recruitment/fetchMyReferrals",
  async (
    filters: { status?: string; page?: number; limit?: number } | undefined,
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getMyReferrals(
        company_id,
        company_type,
        filters,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchHRReferrals = createAsyncThunk(
  "recruitment/fetchHRReferrals",
  async (
    filters:
      | {
          status?: string;
          job_id?: number;
          referred_by?: number;
          date_from?: string;
          date_to?: string;
          page?: number;
          limit?: number;
        }
      | undefined,
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.listHRReferrals(
        company_id,
        company_type,
        filters,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchReferralStatistics = createAsyncThunk(
  "recruitment/fetchReferralStatistics",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getReferralStatistics(
        company_id,
        company_type,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchTopReferrers = createAsyncThunk(
  "recruitment/fetchTopReferrers",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getTopReferrers(company_id, company_type);
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const publishJob = createAsyncThunk(
  "recruitment/publishJob",
  async (
    {
      jobId,
      platforms,
      platformSpecificConfig,
    }: {
      jobId: number;
      platforms: string[];
      platformSpecificConfig?: Record<string, any>;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.publishJob(
        company_id,
        company_type,
        jobId,
        platforms,
        platformSpecificConfig,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const syncExternalJobs = createAsyncThunk(
  "recruitment/syncExternalJobs",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.syncExternalJobs(company_id, company_type);
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);


export const addApplicationNote = createAsyncThunk(
  "recruitment/addApplicationNote",
  async (
    { applicationId, note }: { applicationId: number; note: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.addApplicationNote(
        company_id,
        company_type,
        applicationId,
        note,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const addApplicationTags = createAsyncThunk(
  "recruitment/addApplicationTags",
  async (
    { applicationId, tags }: { applicationId: number; tags: string[] },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.addApplicationTags(
        company_id,
        company_type,
        applicationId,
        tags,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const removeApplicationTag = createAsyncThunk(
  "recruitment/removeApplicationTag",
  async (
    { applicationId, tag }: { applicationId: number; tag: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.removeApplicationTag(
        company_id,
        company_type,
        applicationId,
        tag,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const recalculateATSScore = createAsyncThunk(
  "recruitment/recalculateATSScore",
  async (applicationId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.recalculateATSScore(
        company_id,
        company_type,
        applicationId,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const verifyDocument = createAsyncThunk(
  "recruitment/verifyDocument",
  async (
    {
      documentId,
      status,
      verificationNotes,
    }: {
      documentId: number;
      status: "verified" | "rejected" | string;
      verificationNotes?: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.verifyDocument(
        company_id,
        company_type,
        documentId,
        status,
        verificationNotes,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

interface RequestDocumentDetail {
  type: string;
  is_mandatory: boolean;
  deadline: string | null;
}

const DOCUMENT_NAME_MAP: Record<string, string> = {
  id_proof: "Government ID Proof",
  address_proof: "Address Proof",
  education_certificate: "Education Certificate",
  experience_letter: "Experience Letter",
  salary_slip: "Salary Slips",
  reference_letter: "Reference Letter",
  portfolio: "Portfolio",
  other: "Other Document",
};

export const requestDocument = createAsyncThunk(
  "recruitment/requestDocument",
  async (
    {
      applicationId,
      documentDetails,
      messageText,
    }: {
      applicationId: number;
      documentDetails: RequestDocumentDetail[];
      messageText?: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;

      const results = await Promise.all(
        documentDetails.map((doc) =>
          recruitmentApi.requestDocument(
            company_id,
            company_type,
            applicationId,
            doc.type,
            DOCUMENT_NAME_MAP[doc.type] ?? doc.type,
            {
              description: messageText || undefined,
              is_mandatory: doc.is_mandatory,
              deadline: doc.deadline ?? null,
              send_notification: true,
            },
          ),
        ),
      );

      return results;
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);
export const editInterview = createAsyncThunk(
  "recruitment/editInterview",
  async (
    {
      interviewId,
      data,
    }: {
      interviewId: number;
      data: {
        scheduled_at?: string;
        duration_minutes?: number;
        interview_mode?: string;
        meeting_link?: string;
        meeting_location?: string;
        meeting_platform?: string;
        interviewer_ids?: number[];
        instructions?: string;
        send_notification?: boolean;
      };
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.editInterview(
        company_id,
        company_type,
        interviewId,
        data,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const cancelInterview = createAsyncThunk(
  "recruitment/cancelInterview",
  async (
    {
      interviewId,
      cancellationReason,
      cancellationNotes,
      sendNotification,
    }: {
      interviewId: number;
      cancellationReason:
        | "candidate_unavailable"
        | "interviewer_unavailable"
        | "position_filled"
        | "candidate_withdrawn"
        | "other";
      cancellationNotes?: string;
      sendNotification?: boolean;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.cancelInterview(
        company_id,
        company_type,
        interviewId,
        cancellationReason,
        cancellationNotes,
        sendNotification,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const completeInterview = createAsyncThunk(
  "recruitment/completeInterview",
  async (
    { interviewId, notes }: { interviewId: number; notes?: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.completeInterview(
        company_id,
        company_type,
        interviewId,
        notes,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateInterviewStatus = createAsyncThunk(
  "recruitment/updateInterviewStatus",
  async (
    {
      interviewId,
      newStatus,
      notes,
    }: {
      interviewId: number;
      newStatus: string;
      notes?: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateInterviewStatus(
        company_id,
        company_type,
        interviewId,
        newStatus,
        notes,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchAvailableInterviewers = createAsyncThunk(
  "recruitment/fetchAvailableInterviewers",
  async ({ date }: { date?: string } = {}, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getAvailableInterviewers(
        company_id,
        company_type,
        date,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const submitInterviewFeedback = createAsyncThunk(
  "recruitment/submitInterviewFeedback",
  async (feedback: InterviewFeedbackPayload, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.submitInterviewFeedback(
        company_id,
        company_type,
        feedback,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchInterviewDetails = createAsyncThunk(
  "recruitment/fetchInterviewDetails",
  async (interviewId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getInterviewDetails(
        company_id,
        company_type,
        interviewId,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchAggregateFeedback = createAsyncThunk(
  "recruitment/fetchAggregateFeedback",
  async (interviewId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getAggregateInterviewFeedback(
        company_id,
        company_type,
        interviewId,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateReferralStatus = createAsyncThunk(
  "recruitment/updateReferralStatus",
  async (
    {
      referralId,
      status,
      statusNotes,
      sendNotification,
    }: {
      referralId: number;
      status: string;
      statusNotes?: string;
      sendNotification?: boolean;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateReferralStatus(
        company_id,
        company_type,
        referralId,
        status,
        statusNotes,
        sendNotification,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const convertReferralToApplication = createAsyncThunk(
  "recruitment/convertReferralToApplication",
  async (referralId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.convertReferralToApplication(
        company_id,
        company_type,
        referralId,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const submitReferral = createAsyncThunk(
  "recruitment/submitReferral",
  async (
    data: {
      candidate_name: string;
      candidate_email: string;
      candidate_phone: string;
      job_id: number;
      relationship: string;
      why_good_fit: string;
      linkedin_profile?: string;
      total_experience_years?: number;
      current_company?: string;
      current_designation?: string;
      resume_link?: string;
      skills?: string[];
      additional_notes?: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.submitReferral(
        company_id,
        company_type,
        data,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const approveReferralBonus = createAsyncThunk(
  "recruitment/approveReferralBonus",
  async (
    {
      referralId,
      bonusAmount,
      approvalNotes,
    }: {
      referralId: number;
      bonusAmount: number;
      approvalNotes?: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.approveReferralBonus(
        company_id,
        company_type,
        referralId,
        bonusAmount,
        approvalNotes,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const processReferralPayment = createAsyncThunk(
  "recruitment/processReferralPayment",
  async (
    {
      referralId,
      paymentReference,
      paymentDate,
      paymentNotes,
    }: {
      referralId: number;
      paymentReference?: string;
      paymentDate?: string;
      paymentNotes?: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.processReferralPayment(
        company_id,
        company_type,
        referralId,
        paymentReference,
        paymentDate,
        paymentNotes,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchCareerPageSettings = createAsyncThunk(
  "recruitment/fetchCareerPageSettings",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getCareerPageSettings(
        company_id,
        company_type,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchCareerPageLink = createAsyncThunk(
  "recruitment/fetchCareerPageLink",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getCareerPageLink(company_id, company_type);
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const createCareerPageLinkThunk = createAsyncThunk(
  "recruitment/createCareerPageLink",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.createCareerPageLink(
        company_id,
        company_type,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const toggleCareerPageLinkThunk = createAsyncThunk(
  "recruitment/toggleCareerPageLink",
  async (status: 0 | 1, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.toggleCareerPageLink(
        company_id,
        company_type,
        status,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateCareerPageSettingsThunk = createAsyncThunk(
  "recruitment/updateCareerPageSettings",
  async (settings: Record<string, any>, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateCareerPageSettings(
        company_id,
        company_type,
        settings,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const uploadCareerPageAsset = createAsyncThunk(
  "recruitment/uploadCareerPageAsset",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      return await recruitmentApi.uploadCareerPageAsset(formData);
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const deleteCareerPageAsset = createAsyncThunk(
  "recruitment/deleteCareerPageAsset",
  async (
    { assetType }: { assetType: "logo" | "banner" },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.deleteCareerPageAsset(
        company_id,
        company_type,
        assetType,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchIntegrations = createAsyncThunk(
  "recruitment/fetchIntegrations",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getIntegrations(company_id, company_type);
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const toggleIntegration = createAsyncThunk(
  "recruitment/toggleIntegration",
  async (
    { integrationKey, enabled }: { integrationKey: string; enabled: boolean },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.toggleIntegration(
        company_id,
        company_type,
        integrationKey,
        enabled,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const testIntegrationConnection = createAsyncThunk(
  "recruitment/testIntegrationConnection",
  async (integrationKey: string, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.testIntegrationConnection(
        company_id,
        company_type,
        integrationKey,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateIntegrationCredentials = createAsyncThunk(
  "recruitment/updateIntegrationCredentials",
  async (
    {
      integrationKey,
      apiKey,
      apiSecret,
      accessToken,
    }: {
      integrationKey: string;
      apiKey: string;
      apiSecret: string;
      accessToken: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateIntegrationCredentials(
        company_id,
        company_type,
        integrationKey,
        apiKey,
        apiSecret,
        accessToken,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateSyncFrequency = createAsyncThunk(
  "recruitment/updateSyncFrequency",
  async (
    {
      integrationKey,
      syncFrequencyMinutes,
    }: { integrationKey: string; syncFrequencyMinutes: number },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateSyncFrequency(
        company_id,
        company_type,
        integrationKey,
        syncFrequencyMinutes,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchATSScoringWeights = createAsyncThunk(
  "recruitment/fetchATSScoringWeights",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getATSScoringWeights(
        company_id,
        company_type,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateATSScoringWeightsThunk = createAsyncThunk(
  "recruitment/updateATSScoringWeights",
  async (
    weights: {
      skills_match: number;
      experience_match: number;
      education_match: number;
      keyword_match: number;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateATSScoringWeights(
        company_id,
        company_type,
        weights,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchReferralRewards = createAsyncThunk(
  "recruitment/fetchReferralRewards",
  async (
    filters: {
      status?: string;
      stage?: string;
      search?: string;
      page?: number;
      limit?: number;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.listReferralRewards(
        company_id,
        company_type,
        filters,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateReferralRewardStatus = createAsyncThunk(
  "recruitment/updateReferralRewardStatus",
  async (
    {
      rewardId,
      status,
      notes,
    }: { rewardId: number; status: string; notes?: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateReferralRewardStatus(
        company_id,
        company_type,
        rewardId,
        status,
        notes,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const fetchReferralRewardSummary = createAsyncThunk(
  "recruitment/fetchReferralRewardSummary",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.getReferralRewardSummary(
        company_id,
        company_type,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

// ─────────────────── APPLICATION ROUNDS (PIPELINE) ──────────────────────

export const fetchApplicationRounds = createAsyncThunk(
  "recruitment/fetchApplicationRounds",
  async (applicationId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.listApplicationRounds(
        company_id,
        company_type,
        applicationId,
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);

export const updateApplicationRoundResult = createAsyncThunk(
  "recruitment/updateApplicationRoundResult",
  async (
    {
      applicationRoundId,
      roundStatus,
      score,
      notes,
      resultOverride,
      sendNotification,
    }: {
      applicationRoundId: number;
      roundStatus: string;
      score?: number;
      notes?: string;
      resultOverride?: boolean;
      sendNotification?: boolean;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.recruitment.activeCompany;
      return await recruitmentApi.updateApplicationRoundResult(
        company_id,
        company_type,
        applicationRoundId,
        roundStatus,
        {
          score,
          notes,
          result_override: resultOverride,
          send_notification: sendNotification,
        },
      );
    } catch (error: any) {
      return rejectWithValue(getError(error));
    }
  },
);
